import React, { useCallback, useContext, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import {
    Avatar,
    Button, CircularProgress, IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText, Tooltip, Typography
} from '@mui/material';

import { ApiRoutes, FetchContext, HttpMethods, ToastContext } from '../contexts';

import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';

export const InputFiles = ({
    title,
    image,
    singleFile,
    disabled,
    onFileUploaded,
    customUpload,
    onFileSelect,
    uploadButtonText,
    ...props }) => {
    const { fetchAuthorized } = useContext(FetchContext);
    const { showSuccessMessage } = useContext(ToastContext);

    const [files, setFiles] = useState([]);

    const handleDrop = useCallback((acceptedFiles) => {
        setFiles((prevFiles) => {
            if (singleFile) return [].concat(acceptedFiles[0]);
            else return [...prevFiles].concat(acceptedFiles);
        });
    }, []);

    const [isUploading, setIsUploading] = useState(false);
    const uploadAllFilesClickHandler = async () => {

        setIsUploading(true);
        for (const file of files) {
            try {
                if (customUpload) {
                    const uploadedFile = await onFileSelect(file);
                    setFiles((prevFiles) => [...prevFiles].filter(x => x.name !== file.name));
                    if (uploadedFile) {
                        showSuccessMessage(`File '${file.name}' è stato caricato`);
                    }
                } else {
                    const data = new FormData();
                    data.append("file", file);
                    const r = await fetchAuthorized(image ? ApiRoutes.File.UploadImage() : ApiRoutes.File.UploadFile(),
                        {
                            method: HttpMethods.POST,
                            body: data
                        });

                    const uploadedFile = await r.json();
                    setFiles((prevFiles) => [...prevFiles].filter(x => x.name !== file.name));

                    notifyFileUploaded(uploadedFile);

                    showSuccessMessage(`File '${file.name}' è stato caricato`);
                }
            } catch (e) {
                console.debug(e);
            }
        }

        setIsUploading(false);
    }

    const notifyFileUploaded = (storedFile) => {
        if (onFileUploaded) onFileUploaded(storedFile);
    }

    const removeFileClickHandler = (index) => {
        const newFiles = [].concat(files)
        newFiles.splice(index, 1)
        setFiles(newFiles)
    }

    const removeAllFilesClickHandler = () => {
        setFiles([])
    }

    const {
        getRootProps,
        getInputProps,
        isDragActive
    } = useDropzone({ onDrop: handleDrop, disabled });
    return (
        <div>
            <div
                style={isDragActive ? {
                    border: "1px",
                    padding: "8px",
                    outline: "none",
                    display: "flex",
                    flexWrap: "wrap",
                    backgroundColor: "#e1e1e1",
                    opacity: 0.7,
                } : {
                    border: "1px",
                    padding: "8px",
                    outline: "none",
                    display: "flex",
                    flexWrap: "wrap",
                    backgroundColor: "#e1e1e1",
                    opacity: 1,
                }}
                {...getRootProps()}>
                <input {...getInputProps()} />
                <div>
                    <Typography
                        gutterBottom
                        variant="h6">
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            marginTop: 1
                        }}
                        color="textSecondary"
                        variant="body1">
                        Trascina il file qui o fai clic su
                        {' '}
                        <Link color="textSecondary" underline="always">sfoglia</Link>
                        {' '}
                        nel tuo computer
                    </Typography>
                </div>
            </div>
            {files && files.length > 0 && (
                <>
                    <List sx={{ maxHeight: 320 }}>
                        {files.map((file, i) => (
                            <ListItem
                                disableGutters
                                divider={i < files.length - 1}
                                key={i}>
                                <ListItemIcon>
                                    {!isUploading ?
                                        <>
                                            {image ?
                                                <Avatar sx={{
                                                    width: "48px",
                                                    height: "48px",
                                                    marginRight: 1
                                                }} src={URL.createObjectURL(file)} alt={file.name} />
                                                :
                                                <PublishIcon />
                                            }
                                        </>
                                        :
                                        <CircularProgress size="1.6em" color="primary" />
                                    }
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    primaryTypographyProps={{ variant: 'body1' }}
                                />
                                {!isUploading && !singleFile &&
                                    <Tooltip title="Remove">
                                        <IconButton
                                            color="primary"
                                            edge="end"
                                            onClick={() => removeFileClickHandler(i)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                            </ListItem>
                        ))}
                    </List>
                    <div style={{
                        marginTop: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                        "& > * + *": {
                            marginLeft: 2
                        }
                    }}>
                        {!singleFile &&
                            <Button
                                color="primary"
                                sx={{ marginRight: 10 }}
                                size="small"
                                disabled={disabled && isUploading}
                                onClick={removeAllFilesClickHandler}>
                                Remove all
                            </Button>
                        }
                        <Button
                            color="primary"
                            size="small"
                            variant="contained"
                            disabled={disabled && isUploading}
                            onClick={uploadAllFilesClickHandler}>
                            {uploadButtonText ?? (singleFile ? "Upload del file" : "Upload dei file")}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
